import { ReactNode } from 'react';
import { ColumnPreference } from 'src/services/settings/column-preferences.service';

// ----------------------------------------------------------------------

/**
 * One column in an entity's list registry (JT1-1528). The registry is the single source of truth
 * for what a list can show, search and sort; a user's saved ColumnPreference[] then picks the
 * visible subset and order. `key` is the stable identifier shared across the column pref, the
 * server sort field, and the per-column search field.
 */
export interface ColumnDef<TRow> {
  key: string;
  /** Header label — caller passes an already-translated string. */
  label: string;
  render: (row: TRow) => ReactNode;
  /** Header click issues a server sort on this key. */
  sortable?: boolean;
  /** Shows a per-column search input bound to this key. */
  searchable?: boolean;
  width?: number | string;
  minWidth?: number;
  align?: 'left' | 'right' | 'center';
  /** Shown by default when the user has no saved preference for this key. */
  defaultVisible?: boolean;
  /** Always visible and cannot be hidden or reordered away (e.g. the name column). */
  fixed?: boolean;
}

/**
 * Resolves the registry against a user's saved preferences into the ordered, visible column list to
 * render. Rules:
 *  - fixed columns are always visible and kept ahead of the rest, in registry order;
 *  - a saved preference sets visibility and order for the keys it names, in its own order;
 *  - registry columns with no saved preference fall back to defaultVisible and append in registry
 *    order (so newly-added columns appear without wiping a user's saved layout);
 *  - preferences naming a key no longer in the registry are ignored.
 */
export function resolveColumns<TRow>(
  registry: ColumnDef<TRow>[],
  preferences: ColumnPreference[] | null | undefined
): ColumnDef<TRow>[] {
  const byKey = new Map(registry.map((col) => [col.key, col]));
  const fixed = registry.filter((col) => col.fixed);

  if (!preferences || preferences.length === 0) {
    // No saved layout: fixed + defaultVisible, in registry order.
    return registry.filter((col) => col.fixed || col.defaultVisible !== false);
  }

  const prefKeys = new Set(preferences.map((p) => p.key));

  const fromPrefs = preferences
    .map((pref) => ({ col: byKey.get(pref.key), visible: pref.visible }))
    .filter((entry): entry is { col: ColumnDef<TRow>; visible: boolean } => !!entry.col && !entry.col.fixed)
    .filter((entry) => entry.visible)
    .map((entry) => entry.col);

  // Registry columns the saved layout never mentioned — append by their default.
  const unsaved = registry.filter((col) => !col.fixed && !prefKeys.has(col.key) && col.defaultVisible !== false);

  // Dedupe fixed (already forced to the front) out of the tail.
  const tail = [...fromPrefs, ...unsaved].filter((col) => !col.fixed);

  return [...fixed, ...tail];
}

/** One row in the column picker: a registry column with its current visibility and whether it's locked. */
export interface PickerColumn {
  key: string;
  label: string;
  visible: boolean;
  fixed: boolean;
}

/**
 * Builds the full, ordered picker model (every registry column, not just the visible ones) from the
 * registry and the saved layout, so the picker can show/reorder everything. Order mirrors
 * resolveColumns: fixed first, then saved order, then registry columns the layout predates.
 */
export function buildPickerModel<TRow>(
  registry: ColumnDef<TRow>[],
  preferences: ColumnPreference[] | null | undefined
): PickerColumn[] {
  const toPicker = (col: ColumnDef<TRow>, visible: boolean): PickerColumn => ({
    key: col.key, label: col.label, visible, fixed: !!col.fixed,
  });

  const fixed = registry.filter((col) => col.fixed).map((col) => toPicker(col, true));

  if (!preferences || preferences.length === 0) {
    const rest = registry.filter((col) => !col.fixed).map((col) => toPicker(col, col.defaultVisible !== false));
    return [...fixed, ...rest];
  }

  const byKey = new Map(registry.map((col) => [col.key, col]));
  const prefKeys = new Set(preferences.map((p) => p.key));

  const saved = preferences
    .map((pref) => ({ col: byKey.get(pref.key), visible: pref.visible }))
    .filter((e): e is { col: ColumnDef<TRow>; visible: boolean } => !!e.col && !e.col.fixed)
    .map((e) => toPicker(e.col, e.visible));

  const unsaved = registry
    .filter((col) => !col.fixed && !prefKeys.has(col.key))
    .map((col) => toPicker(col, col.defaultVisible !== false));

  return [...fixed, ...saved, ...unsaved];
}
